import { Box, Grid, List, ListItem, ListItemText } from "@mui/material";
import { ReactComponent as DeleteCircle } from "assets/DeleteCircle.svg";
import { ReactComponent as DeleteIcon } from "assets/DeleteIcon.svg";
import { ReactComponent as AddCircle } from "assets/add-circle-small.svg";
import ThemeContext from "components/app/Context";
import AsyncAutocomplete from "components/common/Autocomplete";
import Button from "components/common/Button";
import Chip from "components/common/Chip";
import DialogFormation from "components/common/DialogFormation";
import Feedback from "components/common/Feedback";
import KebabMenu from "components/common/KebabMenu";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import AvatarItem from "components/common/UserAvatarList/item";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  ABOVE_PROPERTY,
  accessTypes,
  invitee,
  messages,
  multipleAssignee,
  singleAssignee
} from "utils/CommonConstants";
import { createParamforSearch, getUserHltId, matchOwnerWithHltId } from "utils/CommonFunctions";
import { responseStatusTypes } from "utils/Types";
import {
  fetchUserRemoveValidationAsync,
  patchAddInviteeAsync,
  patchUpdateOwnershipAsync,
  removeMemberBulkActionAsync
} from "../MyPlanDetails/MyPlanDetails.slice";
import "./InviteMemberDialog.scss";

interface Prop {
  openInviteMemberDialog: boolean;
  setOpenInviteMemberDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function InviteMemberDialog(props: Prop) {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const { isMobileScreen }: any = useContext(ThemeContext);
  const [assigneeVal, setAssigneeVal] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    title: "",
    description: "",
    subDescription: "",
    primaryButtonLabel: ""
  });
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedUserValidationCheck, setSelectedUserValidationCheck] = useState({});
  const [cardsAssignee, setCardsAssignee] = useState<string[]>([]);
  const { openInviteMemberDialog, setOpenInviteMemberDialog } = props;
  const selectedMyPlanDetail: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const assigneeList: any = useAppSelector((state) => state.myPlanDetails.ad);
  const memberAction = useAppSelector((state) => state.formDetails?.updateOwner?.memberAction);
  const message =
    memberAction?.action === "removed"
      ? `Updated! ${memberAction?.userName} is removed as Owner of this Plan.`
      : `Updated! ${memberAction?.userName} is now an Owner of this Plan.`;
  const selfRemovalMessage = `Ownership update successfull! ${memberAction?.userName} has been ${memberAction?.action} as a owner to this plan.`;
  const { isAdmin, isAbovePropertyUser, userName } = useAppSelector((state) => state.app);
  const isMemberRemoveSuccess = useAppSelector((state) => state.formDetails.removeInvitee.status);

  const planMemberMenu = (isOwner: boolean, ismemberList: boolean) => {
    return [
      {
        label: "Set as Owner",
        icon: AddCircle,
        accessType: accessTypes.delete,
        action: (value: any) => {
          const owners = [...(selectedMyPlanDetail?.owners || []), value?.hltId];
          dispatch(
            patchUpdateOwnershipAsync({
              id: selectedMyPlanDetail.id,
              ownersList: { owners: owners },
              memberAction: {
                userName: value?.userName,
                action: "added",
                hltId: value?.hltId
              }
            })
          );
        },
        enabled: (isAdmin || isAbovePropertyUser || isOwner) && ismemberList
      },
      {
        label: "Remove Ownership",
        icon: DeleteCircle,
        accessType: accessTypes.delete,
        action: (value: any) => {
          if (selectedMyPlanDetail?.owners?.length === 1) {
            dispatch(
              setFeedbackDetails({
                shouldShow: true,
                type: "plan",
                setState: setFeedbackDetails({ shouldShow: false }),
                message: messages.mustHaveSingleOwner
              })
            );
            return;
          }
          const list = selectedMyPlanDetail?.owners.filter((hltId: any) => hltId !== value?.hltId);
          dispatch(
            patchUpdateOwnershipAsync({
              id: selectedMyPlanDetail.id,
              ownersList: { owners: list },
              memberAction: {
                userName: value?.userName,
                action: "removed",
                hltId: value?.hltId
              }
            })
          );
        },
        enabled: (isAdmin || isAbovePropertyUser || isOwner) && !ismemberList
      },
      {
        label: "Remove from Plan",
        icon: DeleteIcon,
        accessType: accessTypes.update,
        action: (value: any) => {
          confirmationSet(value?.hltId);
        },
        enabled: ismemberList
      }
    ];
  };

  useEffect(() => {
    setCardsAssignee([]);
    selectedMyPlanDetail?.sections?.map((item: any) => {
      return item?.cards?.map((item2: any) => {
        if (item2?.assignee?.length > 0) setCardsAssignee([...cardsAssignee, ...(item2?.assignee || [])]);
        return null;
      });
    });
    setConfirmDialog(false);
  }, [openInviteMemberDialog, selectedMyPlanDetail]);

  useEffect(() => {
    if (
      memberAction?.action === "removed" &&
      memberAction?.hltId === getUserHltId() &&
      !selectedMyPlanDetail?.members.includes(memberAction?.hltId)
    ) {
      navigate(`/`, {
        state: {
          hasNoAccess: true,
          message: selfRemovalMessage,
          variant: "success"
        }
      });
    }
  }, [memberAction]);

  useEffect(() => {
    if (isMemberRemoveSuccess === responseStatusTypes.success && selectedUser === userName?.hltId) {
      selectedMyPlanDetail?.innCode === ABOVE_PROPERTY
        ? navigate(`/myplans?parentTab=Template+Plans`)
        : navigate(`/myplans`);
    }
  }, [isMemberRemoveSuccess]);

  const handleCloseInviteMemberDialog = () => {
    setAssigneeVal([]);
    setOpenInviteMemberDialog(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog(false);
  };

  const confirmationSet = async (hltId: string) => {
    setSelectedUser(hltId);
    const validationRes = await dispatch(fetchUserRemoveValidationAsync({ planId: selectedMyPlanDetail.id, hltId }));
    setSelectedUserValidationCheck(validationRes);
    if (validationRes?.payload?.userRemovalType === multipleAssignee) {
      setDialogContent({
        title: translate("myPlan.removeUser.popup.heading"),
        description: translate("myplan.removeAssigee.popup.desc"),
        subDescription: "",
        primaryButtonLabel: translate("common.proceed")
      });
      setConfirmDialog(true);
    } else if (validationRes?.payload?.userRemovalType === singleAssignee) {
      setDialogContent({
        title: translate("myPlan.reassignOwner"),
        description: translate("myPlan.removeUser.popup.desc"),
        subDescription: "",
        primaryButtonLabel: translate("common.proceed")
      });
      setConfirmDialog(true);
    } else removeInviteeAction(validationRes, hltId);
  };

  const formPayload = (validationRes: any, userhltId: string) => {
    if ([multipleAssignee, invitee].includes(validationRes?.payload?.userRemovalType)) {
      return {
        updateType: "REMOVE_PLAN_MEMBER",
        userToBeRemoved: userhltId,
        sections: []
      };
    } else {
      const collectedData = [];
      for (const [id, value] of Object.entries<[]>(validationRes?.payload?.sectionCardMappings)) {
        collectedData.push({
          id,
          cardModels: value?.map((item: any) => {
            return {
              id: item,
              assignee: [validationRes?.payload?.oldestOwner]
            };
          })
        });
      }
      return {
        updateType: "REMOVE_PLAN_MEMBER",
        userToBeRemoved: userhltId,
        sections: collectedData
      };
    }
  };

  const removeInviteeAction = (validationCheck: object, userhltId: string) => {
    const payload = formPayload(validationCheck, userhltId);
    dispatch(removeMemberBulkActionAsync({ payload, planId: selectedMyPlanDetail.id }));
    setConfirmDialog(false);
  };

  const inviteMember = () => {
    dispatch(
      patchAddInviteeAsync({
        id: selectedMyPlanDetail.id,
        inviteeList: {
          members: selectedMyPlanDetail?.members
            ? [...(selectedMyPlanDetail?.members || []), ...assigneeVal]
            : [...assigneeVal]
        }
      })
    );
  };

  return (
    <>
      <Feedback form="updateOwner" message={message} />
      {openInviteMemberDialog && (
        <DialogFormation
          title={confirmDialog ? dialogContent?.title : "Invite Member(s)"}
          openDialog={openInviteMemberDialog}
          handleClose={confirmDialog ? handleCloseConfirmDialog : handleCloseInviteMemberDialog}
          actionConfig={
            <div className="modal-config-wrapper">
              {confirmDialog && (
                <>
                  <Button variant="secondary" label="Cancel" onClick={handleCloseConfirmDialog} />
                  <Button
                    label={dialogContent?.primaryButtonLabel}
                    onClick={() => removeInviteeAction(selectedUserValidationCheck, selectedUser)}
                  />
                </>
              )}
              {!confirmDialog && (
                <Button
                  disabled={assigneeVal?.length === 0}
                  variant="primary"
                  label="Invite"
                  accessType={accessTypes.update}
                  onClick={inviteMember}
                />
              )}
            </div>
          }
        >
          {confirmDialog ? (
            <>
              {dialogContent?.description}
              <Box>{dialogContent?.subDescription}</Box>
            </>
          ) : (
            <Grid
              container
              alignItems={"center"}
              justifyContent="center"
              textAlign={"center"}
              display={"flex"}
              flexDirection="column"
              gap={"16px"}
              width={"100%"}
              marginTop="24px"
            >
              <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
                {translate("myPlan.inviteMember.popup")}
              </Typography>
              <Grid
                container
                item
                paddingX={0}
                alignItems={"center"}
                justifyContent="center"
                textAlign={"center"}
                display={"flex"}
                flexDirection="row"
              >
                <Grid item xs={12} sm={12}>
                  <AsyncAutocomplete
                    getUserDetail={(val) => {
                      setAssigneeVal(val);
                    }}
                    assignedMember={assigneeList}
                    placeholder="Email address or name."
                    searchParams={{ innCode: createParamforSearch(isAdmin, selectedMyPlanDetail?.innCode) }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={3}
                  display="flex"
                  justifyContent={isMobileScreen ? "center" : "flex-end"}
                  sx={isMobileScreen ? { marginTop: "10px" } : {}}
                ></Grid>
              </Grid>
              <List sx={{ width: "100%" }}>
                {assigneeList?.map((item: any) => (
                  <ListItem key={item.hltId} sx={{ paddingRight: "0px" }}>
                    <ListItemText
                      primary={
                        <div className="list-wrapper">
                          <div className="name-and-icon">
                            <AvatarItem size="regular" user={item} variant="neutral" />
                            <Box
                              display={"flex"}
                              flexDirection="column"
                              justifyContent={"center"}
                              alignItems="flex-start"
                            >
                              <Typography variant={"body2"} showBold>
                                {item?.displayName}
                                {selectedMyPlanDetail?.owners?.includes(item?.hltId) && <></>}
                              </Typography>
                              <Typography variant={isMobileScreen ? "label" : "body2"}>{item?.email}</Typography>
                            </Box>
                          </div>
                          <div className="action-tags">
                            <div className="assignee-chips">
                              {selectedMyPlanDetail?.owners?.includes(item?.hltId) && (
                                <Chip
                                  backgroundColor={tokens.Background.Feedback.Success}
                                  color={tokens.Text.Feedback.Done}
                                  sx={{ marginLeft: "8px" }}
                                  label={"Plan Owner"}
                                ></Chip>
                              )}
                            </div>
                            <div>
                              <KebabMenu
                                value={{ hltId: item?.hltId, userName: item?.displayName }}
                                options={planMemberMenu(
                                  matchOwnerWithHltId(selectedMyPlanDetail?.owners),
                                  !selectedMyPlanDetail?.owners?.includes(item?.hltId)
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}
        </DialogFormation>
      )}
    </>
  );
}
